import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store';
import { fetchUsers, uploadUsersFile, setSelectedUser, fetchUserDebts } from '../../store/slices/usersSlice';
import { downloadUserTemplate } from '../../services/api/usersApi';
import { deleteConsumerByPhone } from '../../services/api/userService';
import { useTranslation } from '../../hooks/useTranslation';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import DebtDetailsModal from '../DebtDetailsModal';
import FileUploadModal from '../FileUploadModal';
import CreateConsumerModal from '../CreateConsumerModal';
import NotificationModal from '../NotificationModal';
import './UsersTab.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface UsersTabProps {
  onBack: () => void;
}

const UsersTab: React.FC<UsersTabProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { users, loading, error } = useAppSelector((state) => state.users);
  const { userId: creatorId, userType } = useAppSelector((state: RootState) => state.auth);

  // Filter to show only CONSUMER user type
  const consumers = useMemo(() => {
    return users.filter((user) => user.userType === 'CONSUMER');
  }, [users]);

  const isTwizeAdmin = userType === 'TWIZE_ADMIN';
  const [deletingPhoneNumber, setDeletingPhoneNumber] = useState<string | null>(null);
  
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateConsumerModal, setShowCreateConsumerModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    // Use mock data if API fails - replace with actual dispatch
    dispatch(fetchUsers()).catch(() => {
      // Fallback to mock data
      console.log('Using mock data');
    });
  }, [dispatch]);

  const handleViewDebts = (user: typeof users[0]) => {
    dispatch(setSelectedUser(user));
    dispatch(fetchUserDebts(user.id));
    setShowDebtModal(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadUserTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'user_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading template:', error);
      
      // Check if error response contains specific message
      if (error.response?.data) {
        // Try to read the error message from blob
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result as string);
            if (errorData.message?.includes('bankName')) {
              setNotification({
                isOpen: true,
                title: t('consumers.messages.downloadFailed'),
                message: t('consumers.messages.downloadFailedNoBank'),
                type: 'error'
              });
            } else {
              setNotification({
                isOpen: true,
                title: t('consumers.messages.downloadFailed'),
                message: `${t('consumers.messages.downloadFailed')}: ${errorData.message || 'Unknown error'}`,
                type: 'error'
              });
            }
          } catch {
            setNotification({
              isOpen: true,
              title: t('consumers.messages.downloadFailed'),
              message: t('consumers.messages.downloadFailedGeneric'),
              type: 'error'
            });
          }
        };
        reader.readAsText(error.response.data);
      } else {
        setNotification({
          isOpen: true,
          title: t('consumers.messages.downloadFailed'),
          message: t('consumers.messages.downloadFailedGeneric'),
          type: 'error'
        });
      }
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      setUploadLoading(true);
      await dispatch(uploadUsersFile(file)).unwrap();
      setNotification({
        isOpen: true,
        title: t('consumers.messages.uploadSuccess'),
        message: t('consumers.messages.uploadSuccess'),
        type: 'success'
      });
      setShowUploadModal(false);
      // Refresh users list
      dispatch(fetchUsers());
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setNotification({
        isOpen: true,
        title: t('consumers.messages.uploadFailed'),
        message: error || t('consumers.messages.uploadFailed'),
        type: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleConsumerCreated = () => {
    setNotification({
      isOpen: true,
      title: t('consumers.messages.consumerCreated'),
      message: t('consumers.messages.consumerCreated'),
      type: 'success'
    });
    // Refresh users list
    dispatch(fetchUsers());
  };

  const handleDeleteConsumer = useCallback(
    async (user: { phoneNumber?: string; firstName?: string; lastName?: string }) => {
      const phone = user?.phoneNumber?.trim();
      if (!phone) return;
      if (!creatorId) {
        setNotification({
          isOpen: true,
          title: t('common.error'),
          message: 'Creator ID not found. Please log in again.',
          type: 'error'
        });
        return;
      }
      if (!window.confirm(t('consumers.messages.deleteConsumerConfirm', { phoneNumber: phone }))) return;
      setDeletingPhoneNumber(phone);
      try {
        const result = await deleteConsumerByPhone(phone, creatorId);
        if (result.success) {
          setNotification({
            isOpen: true,
            title: t('consumers.messages.consumerDeleted'),
            message: result.message || t('consumers.messages.consumerDeleted'),
            type: 'success'
          });
          dispatch(fetchUsers());
        } else {
          setNotification({
            isOpen: true,
            title: t('consumers.messages.deleteConsumerFailed'),
            message: result.message || t('consumers.messages.deleteConsumerFailed'),
            type: 'error'
          });
        }
      } catch (e: any) {
        setNotification({
          isOpen: true,
          title: t('consumers.messages.deleteConsumerFailed'),
          message: e?.message || t('consumers.messages.deleteConsumerFailed'),
          type: 'error'
        });
      } finally {
        setDeletingPhoneNumber(null);
      }
    },
    [creatorId, dispatch, t]
  );

  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: 'firstName',
      headerName: t('consumers.table.firstName'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-'
    },
    {
      field: 'lastName',
      headerName: t('consumers.table.lastName'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-'
    },
    {
      field: 'nni',
      headerName: t('consumers.table.nni'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true
    },
    {
      field: 'phoneNumber',
      headerName: t('consumers.table.phoneNumber'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-'
    },
    {
      field: 'bankName',
      headerName: t('consumers.table.bank'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-'
    },
    {
      field: 'userType',
      headerName: t('consumers.table.userType'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => {
        return React.createElement('span', { className: 'user-type-badge' }, params.value || 'CONSUMER');
      }
    },
    {
      field: 'availableBalance',
      headerName: t('consumers.table.availableBalance'),
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: ({ value, filterText }: any) => {
          if (!filterText) return true;
          const cellValue = value?.toString() || '0.00';
          return cellValue.includes(filterText);
        },
        debounceMs: 200
      },
      floatingFilter: true,
      sortable: true,
      cellStyle: { fontWeight: 'bold' } as any,
      cellRenderer: (params: any) => {
        const value = params.value?.toFixed(2) || '0.00';
        return `${value} MRU`;
      },
      valueFormatter: (params: any) => {
        const value = params.value?.toFixed(2) || '0.00';
        return `${value} MRU`;
      }
    },
    {
      field: 'actions',
      headerName: t('consumers.table.actions'),
      sortable: false,
      filter: false,
      width: 260,
      minWidth: 260,
      maxWidth: 260,
      suppressSizeToFit: true,
      pinned: 'right',
      cellStyle: { overflow: 'visible', minWidth: 260 } as any,
      cellClass: 'users-tab-actions-cell',
      cellRenderer: (params: any) => {
        const ctx = params.context || {};
        const data = params.data;
        if (!data) return null;
        return React.createElement(
          'div',
          { className: 'users-tab-actions' },
          React.createElement(
            'button',
            {
              type: 'button',
              className: 'action-cell-btn view-debts-btn',
              onClick: () => ctx.onViewDebts?.(data)
            },
            ctx.t?.('consumers.viewDebts') ?? 'Dettes'
          ),
          ctx.isTwizeAdmin &&
            React.createElement('button', {
              type: 'button',
              className: 'action-cell-btn delete-btn',
              onClick: () => ctx.onDelete?.(data),
              disabled: ctx.deletingPhoneNumber === data?.phoneNumber,
              children:
                ctx.deletingPhoneNumber === data?.phoneNumber
                  ? (ctx.t?.('consumers.deletingConsumer') ?? 'Suppression...')
                  : (ctx.t?.('consumers.deleteConsumer') ?? 'Supprimer')
            })
        );
      }
    }
  ], [t]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    flex: 1,
    minWidth: 100,
    cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis' } as any
  }), []);

  const selectedUser = useAppSelector((state) => state.users.selectedUser);

  return (
    <div className="users-tab">
      <div className="tab-header">
        <button className="back-button" onClick={onBack}>
          ← {t('common.back')}
        </button>
        <h1>{t('consumers.title')}</h1>
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="action-buttons">
        <button 
          className="action-button create-button"
          onClick={() => setShowCreateConsumerModal(true)}
        >
          + {t('consumers.createConsumer')}
        </button>
        <button 
          className="action-button download-button"
          onClick={handleDownloadTemplate}
        >
          {t('consumers.downloadTemplate')}
        </button>
        <button 
          className="action-button upload-button"
          onClick={() => setShowUploadModal(true)}
        >
          {t('consumers.uploadUsers')}
        </button>
      </div>

      <div className="table-container ag-theme-quartz modern-ag-theme" style={{ height: '600px', width: '100%' }}>
        {loading ? (
          <div className="loading-message">{t('consumers.loading')}</div>
        ) : consumers.length === 0 ? (
          <div className="empty-message">{t('consumers.empty')}</div>
        ) : (
          <AgGridReact
            rowData={consumers}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={20}
            suppressCellFocus={true}
            domLayout="normal"
            context={{
              t,
              isTwizeAdmin,
              onViewDebts: handleViewDebts,
              onDelete: handleDeleteConsumer,
              deletingPhoneNumber
            }}
          />
        )}
      </div>

      {showDebtModal && selectedUser && (
        <DebtDetailsModal
          user={selectedUser}
          onClose={() => {
            setShowDebtModal(false);
            dispatch(setSelectedUser(null));
          }}
        />
      )}

      {showUploadModal && (
        <FileUploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadFile}
          loading={uploadLoading}
          accept=".xlsx,.xls"
        />
      )}

      {showCreateConsumerModal && (
        <CreateConsumerModal
          onClose={() => setShowCreateConsumerModal(false)}
          onSuccess={handleConsumerCreated}
        />
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, isOpen: false })}
      />
    </div>
  );
};

export default UsersTab;

